/**
 * /what-is-product-management — SEO glossary/hub page for "what is product
 * management", "product management process", "product management
 * framework", "what is digital product management" (~330 aggregate, KD
 * high but low-effort hub value). Defines the discipline plainly and acts
 * as a topical-authority hub linking the roadmap/strategy/discovery/
 * prioritization pages + the PM-role blog. Light Draftlize turn.
 *
 * English-only + inline. Chinese variant in WhatIsProductManagementZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function WhatIsProductManagement() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/what-is-product-management"
      documentTitle="What Is Product Management? A Plain Guide · Draftlize"
      documentDescription="Product management is owning the what and why of a product — not the building. The role, the process, the frameworks, and how it differs from PO and project management."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Product management</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 62px)', lineHeight: 1.03, margin: '12px 0 24px' }}>
            What is product management?<br/>
            <em>Owning the what and the why.</em>
          </h1>
          <p className="lede">
            <em>Product management</em> is the discipline of deciding <em>what</em> to build and <em>why</em> — and making sure it actually ships and matters — without owning the building itself. A product manager sits between users, the business, and engineering, turning a messy field of needs into a defensible plan. This page defines the discipline plainly: the role, the process from idea to launch, the frameworks PMs lean on, and how product management differs from a product owner or a project manager.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">So, <em>what is product management?</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            Product management is the practice of maximizing the value a product delivers — by choosing the right problems, framing them clearly, and steering the work without a hierarchy that lets you just give orders. The PM owns outcomes, not output: not &quot;we shipped the feature&quot; but &quot;the thing we shipped moved the number we cared about.&quot; For the day-to-day of the role, see <a href="/blog/what-do-product-managers-do" style={{ color: 'var(--amber)' }}>what product managers actually do</a>.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            <em>Digital product management</em> is the same discipline applied to software products specifically, where continuous delivery and data make the loop from idea to learning much tighter. The fundamentals don&apos;t change — only the cadence does.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The process</span>
            <h2 className="serif">From idea to launch, <em>the stages.</em></h2>
          </div>
          <p>Product management isn&apos;t linear, but the work clusters into recognizable stages. Each links to a deeper guide.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Strategy', hint: 'Why & for whom', body: 'Decide who you serve, the problem, why your approach wins, and what you won\'t do. Everything downstream is in service of this.', href: '/product-strategy' },
            { k: 'Discovery', hint: 'What\'s worth it', body: 'Talk to users, test assumptions, retire the risk of building the wrong thing — before committing engineering to it.', href: '/product-discovery' },
            { k: 'Prioritization', hint: 'What first', body: 'Turn more good ideas than capacity into a defensible order, using a framework you can repeat to a skeptic.', href: '/prioritization-frameworks' },
            { k: 'Roadmap', hint: 'What & when', body: 'Express the plan as themes or outcomes over time, honest about certainty, loose enough not to lie about next quarter.', href: '/what-is-a-product-roadmap' },
            { k: 'Definition', hint: 'The spec', body: 'Write the PRD and specs that turn a decision into something a team and an engineer can build without a meeting.', href: '/product-requirements-document' },
            { k: 'Launch & learn', hint: 'Did it work', body: 'Ship, measure against the success metric decided up front, and feed what you learn back into strategy and discovery.', href: '/product-launch-checklist' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <a href={f.href} className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2, textDecoration: 'none' }}>{f.k} →</a>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Don&apos;t confuse the roles</span>
            <h2 className="serif">PM vs product owner <em>vs project manager.</em></h2>
          </div>
          <p>Three roles people blur constantly. The distinction is about what each one owns.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Owns</th>
              <th>Horizon</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Product manager</td><td className="yes">The what &amp; why; outcomes</td><td>Strategy → launch</td></tr>
            <tr><td>Product owner</td><td className="partial">The backlog &amp; sprint scope (Scrum)</td><td>Sprint-to-sprint</td></tr>
            <tr><td>Project manager</td><td className="partial">Timeline, resources, delivery</td><td>A defined project</td></tr>
          </tbody>
        </table>
        <p style={{ maxWidth: 760, marginTop: 18, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>
          The short version: a PM owns whether you&apos;re building the right thing; a product owner (a Scrum role) owns the backlog that feeds delivery; a project manager owns getting a defined scope shipped on time. In small teams one person wears all three hats — which is exactly when the wires get crossed.
        </p>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The thread that holds it together — <em>and breaks.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Every stage above produces decisions, and each stage rests on the one before it. The hard part of the job isn&apos;t any single stage — it&apos;s keeping the thread from strategy through discovery, roadmap, and spec intact as things change. That&apos;s the narrow thing Draftlize is built for.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Decisions become addressable cards</h3>
            <p>A strategy bet, a discovery finding, a prioritization call, a spec requirement — each a typed card with a stable ID, citable across the whole chain instead of buried in six different docs.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one, dependents flag stale</h3>
            <p>The work of product management is mostly keeping things consistent as reality moves. When an upstream decision changes, everything downstream that depended on it auto-flags stale — so the consistency is tracked by the substrate, not by the PM&apos;s memory.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your AI agent shares the context</h3>
            <p>Increasingly PMs run this loop with AI agents. Draftlize is the one shared context Claude Code or Cursor reads and writes over MCP — so the agent helping with the next spec already knows the strategy and decisions the rest of the work is built on.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Product management is owning the what and the why. Most of the job is keeping them true as everything downstream moves.
        </blockquote>
        <cite>Do the work your way. Keep the thread from strategy to spec from quietly snapping.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What does a product manager actually do?', a: 'A PM decides what to build and why, then keeps everyone aligned on it — through strategy, discovery, prioritization, the roadmap, the PRD, and launch. They own outcomes, not code. The day-to-day is a mix of customer conversations, decisions, writing, and saying no. See our full guide on what product managers do.' },
            { q: 'What is the difference between product management and project management?', a: 'Product management owns the what and why — choosing the right problems and outcomes. Project management owns delivery — getting a defined scope shipped on time and on budget. A PM asks "are we building the right thing"; a project manager asks "are we building the thing right, on schedule."' },
            { q: 'What is digital product management?', a: 'It is product management applied specifically to software products, where continuous delivery and analytics make the loop from idea to learning much faster. The discipline is the same; the cadence and the role of data are what differ.' },
            { q: 'What frameworks do product managers use?', a: 'Common ones include prioritization frameworks (RICE, MoSCoW, WSJF), discovery techniques (opportunity solution trees, assumption mapping), and strategy frameworks. The skill is matching the framework to the decision, not adopting one and forcing everything through it.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Run product work with one shared context <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Strategy, discovery, roadmap, spec — Draftlize holds the decisions across all of them as cards, so a change in one flags everything downstream, and your AI agent reads the current version on every turn.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
